/**
 * Hours selectors (PX-W6.4). Derived views (own rows, submitted rows) are
 * memoised with `createSelector` and take their filter argument as the SECOND
 * selector input — never filtered inline in a component. Filtering ROWS is
 * presentational (plan decision 5); no selector computes money.
 */

import { createSelector } from '@reduxjs/toolkit'

import type { HoursEntry } from './types'
import type { HoursState } from './slices/hoursSlice'

interface WithHours {
  hours: HoursState
}

/**
 * The shared EMPTY list — a stable reference so `?? EMPTY` never allocates a
 * fresh array per selector call (which would defeat `createSelector`'s input
 * memoisation and re-render every observer on each store tick).
 */
const EMPTY_ENTRIES: HoursEntry[] = []

/** One project's entries — EMPTY before the first load, never undefined. */
export const selectProjectEntries = (s: WithHours, projectId: string): HoursEntry[] =>
  s.hours.byProject[projectId] ?? EMPTY_ENTRIES

/** The caller's own cross-project entries. */
export const selectMyHours = (s: WithHours): HoursEntry[] => s.hours.mine

/**
 * The signed-in user's own entries of one project — the timesheet list. The
 * author comparison is presentational only; the server enforces self-only
 * writes regardless.
 *
 * ⚠ `principalKey` MUST be the caller's canonical hex
 * (`selectSessionPrincipalKey` — `idHex ?? id`), never the session SLUG:
 * `HoursEntryView.userId` is stamped in hex, so a slug comparison matches
 * nothing and this returns an empty list while the rows exist on the server.
 */
export const selectOwnProjectEntries = createSelector(
  [
    selectProjectEntries,
    (_s: WithHours, _projectId: string, principalKey: string | null) => principalKey
  ],
  (entries, principalKey): HoursEntry[] =>
    principalKey === null ? [] : entries.filter((entry) => entry.userId === principalKey)
)

/** The submitted entries of one project — the approvals queue (presentational filter). */
export const selectSubmittedByProject = createSelector(
  [selectProjectEntries],
  (entries): HoursEntry[] => entries.filter((entry) => entry.status === 'submitted')
)

/** The project list's loading state. */
export const selectProjectHoursLoading = (s: WithHours): boolean => s.hours.projectCache.loading

/** The project list's error (fetch or mutation). */
export const selectHoursError = (s: WithHours) => s.hours.error

/** The log form's in-flight flag. */
export const selectHoursCreating = (s: WithHours): boolean => s.hours.creating

/** Entry ids with a mutation in flight — drives per-row affordances. */
export const selectHoursMutatingIds = (s: WithHours): readonly string[] => s.hours.mutatingIds
